import { ORDER_STATUS_LABELS } from '../constants';
import { splitDateTime } from './index';

// Hollanda posta kodu regex: 4 rakam, opsiyonel boşluk, 2 harf (örn: 2572 HJ, 3532HK)
const DUTCH_POSTCODE_REGEX = /\b(\d{4})\s*([A-Za-z]{2})\b/i;

// Yaygın Hollanda şehirleri (case-insensitive eşleşme için)
const KNOWN_DUTCH_CITIES = [
  'Den Haag', "'s-Gravenhage", 'The Hague', 'Utrecht', 'Amsterdam', 'Rotterdam',
  'Eindhoven', 'Groningen', 'Tilburg', 'Almere', 'Breda', 'Nijmegen', 'Arnhem',
  'Haarlem', 'Amersfoort', 'Zaanstad', 'Zaandam', "'s-Hertogenbosch", 'Den Bosch',
  'Zwolle', 'Zoetermeer', 'Leiden', 'Maastricht', 'Dordrecht', 'Ede', 'Alphen aan den Rijn',
  'Leeuwarden', 'Alkmaar', 'Delft', 'Venlo', 'Deventer', 'Helmond', 'Oss', 'Amstelveen',
  'Hilversum', 'Heerlen', 'Spijkenisse', 'Hengelo', 'Purmerend', 'Roosendaal',
  'Schiedam', 'Lelystad', 'Vlaardingen', 'Gouda', 'Velsen', 'Hoorn', 'Nieuwegein',
  'Veenendaal', 'Zeist', 'Doetinchem', 'Den Helder', 'Rijswijk', 'Voorburg', 'Wassenaar',
  'Westland', 'Naaldwijk', 'Wateringen', 'Monster', 'Capelle aan den IJssel', 'Barendrecht'
];

// Mağaza başlangıç adresi (Google Maps rota başlangıcı için)
export const STORE_ADDRESS = 'Kaapstraat 12, 2572 HJ Den Haag, Nederland';

/**
 * Müşteri nesnesinden akıllı lokasyon (şehir, posta kodu, bölge grubu) çıkarır.
 */
export const parseCustomerLocation = (customer = {}) => {
  const address = (customer.address || '').trim();
  const explicitCity = (customer.city || '').trim();
  const explicitPostalCode = (customer.postalCode || customer.postcode || '').trim();

  let postalCode = explicitPostalCode;
  let regionPrefix = '';

  const pcMatch = address.match(DUTCH_POSTCODE_REGEX);
  if (pcMatch) {
    if (!postalCode) {
      postalCode = `${pcMatch[1]} ${pcMatch[2].toUpperCase()}`;
    }
    regionPrefix = pcMatch[1].substring(0, 2);
  }

  // 1. Şehir: Müşteride açıkça belirtilmişse
  let city = explicitCity;

  // 2. Posta kodundan hemen sonra gelen metin (Hollanda standardı: "2572 HJ Den Haag")
  if (!city && pcMatch) {
    const afterPostcode = address.substring(pcMatch.index + pcMatch[0].length).trim();
    const cleanAfter = afterPostcode.replace(/^[,\-\s]+/, '').split(/[,\n]/)[0].trim();
    if (cleanAfter && cleanAfter.length >= 2 && cleanAfter.length <= 30 && !/^\d+$/.test(cleanAfter)) {
      city = cleanAfter;
    }
  }

  // 3. Bilinen şehir isimleri taraması
  if (!city && address) {
    for (const kc of KNOWN_DUTCH_CITIES) {
      const escaped = kc.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
      const reg = new RegExp(`\\b${escaped}\\b`, 'i');
      if (reg.test(address)) {
        city = kc;
        break;
      }
    }
  }

  // 4. Adresin son virgül/satır parçası (Fallback)
  if (!city && address) {
    const parts = address.split(/[,\n]/).map(p => p.trim()).filter(Boolean);
    if (parts.length > 1) {
      const last = parts[parts.length - 1];
      const cleanedLast = last.replace(DUTCH_POSTCODE_REGEX, '').trim();
      if (cleanedLast.length >= 2 && cleanedLast.length <= 25 && !/^\d+$/.test(cleanedLast)) {
        city = cleanedLast;
      }
    }
  }

  // Bölge Grubu Oluşturma:
  // Şehir varsa öncelikli olarak Şehir adını kullanır.
  // Posta kodu 2 basamaklı prefix varsa ekler (örn: "Utrecht (35xx)")
  let regionKey = 'diger';
  let regionDisplay = 'Diğer / Belirtilmemiş';

  if (city) {
    // Şehir ismini standartlaştır (örn: 'den haag')
    const normalizedCity = city.toLowerCase();
    regionKey = normalizedCity;
    regionDisplay = regionPrefix ? `${city} (${regionPrefix}xx)` : city;
  } else if (regionPrefix) {
    regionKey = `reg-${regionPrefix}`;
    regionDisplay = `Bölge ${regionPrefix}xx`;
  }

  return {
    city: city || '',
    postalCode: postalCode || '',
    regionPrefix: regionPrefix || '',
    regionKey,
    regionDisplay,
    fullAddress: address
  };
};

/**
 * Tarih değerini Date objesine çevirir (saat 00:00:00 sıfırlanmış olarak karşılaştırma için)
 */
export const toNormalizedDate = (dateVal) => {
  if (!dateVal) return null;
  let d;
  if (typeof dateVal?.toDate === 'function') {
    d = dateVal.toDate();
  } else if (dateVal instanceof Date) {
    d = new Date(dateVal);
  } else if (typeof dateVal === 'string') {
    const { date } = splitDateTime(dateVal);
    if (date) {
      const parts = date.split('-');
      if (parts.length === 3) {
        d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
      } else {
        d = new Date(dateVal);
      }
    } else {
      d = new Date(dateVal);
    }
  } else {
    d = new Date(dateVal);
  }

  if (isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Orijinal saat bilgisini string olarak döner (varsa "14:30")
 */
export const extractTimeString = (dateVal) => {
  if (!dateVal) return '';
  const { time } = splitDateTime(dateVal);
  return time || '';
};

/**
 * Tarih ve saati güzel ve okunabilir formatlar
 */
export const formatScheduleDateDisplay = (dateVal, lang = 'tr') => {
  if (!dateVal) return '-';
  const { date, time } = splitDateTime(dateVal);
  if (!date) return '-';

  try {
    const parts = date.split('-');
    if (parts.length === 3) {
      const day = parts[2];
      const monthIndex = parseInt(parts[1], 10) - 1;
      const year = parts[0];
      const monthNamesTr = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
      const monthNamesNl = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
      const monthNames = lang === 'nl' ? monthNamesNl : monthNamesTr;
      const monthStr = monthNames[monthIndex] || parts[1];
      const datePart = `${day} ${monthStr} ${year}`;
      return time ? `${datePart} • ${time}` : datePart;
    }
  } catch (e) {
    // fallback
  }
  return String(dateVal);
};

/**
 * Tek bir adres veya birden fazla adres için Google Maps yol tarifi URL'i üretir.
 * @param {string|string[]} addresses - Adres listesi
 * @param {Object|boolean} options - { startFromStore: boolean } veya boolean (varsayılan: false, kullanıcının mevcut konumundan başlar)
 */
export const generateGoogleMapsUrl = (addresses = [], options = {}) => {
  const validAddresses = (Array.isArray(addresses) ? addresses : [addresses])
    .map(a => (typeof a === 'string' ? a.trim() : ''))
    .filter(Boolean);

  if (validAddresses.length === 0) {
    return 'https://maps.google.com';
  }

  const { startFromStore = false } = typeof options === 'boolean' ? { startFromStore: options } : options;

  if (validAddresses.length === 1) {
    if (startFromStore) {
      return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(STORE_ADDRESS)}&destination=${encodeURIComponent(validAddresses[0])}&travelmode=driving`;
    }
    // Origin belirtilmediğinde Google Haritalar cihazın mevcut konumunu (Your Location) başlangıç noktası alır
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(validAddresses[0])}&travelmode=driving`;
  }

  // Çoklu durak rotası
  const destination = encodeURIComponent(validAddresses[validAddresses.length - 1]);
  const intermediateWaypoints = validAddresses.slice(0, validAddresses.length - 1);
  const waypoints = intermediateWaypoints
    .slice(0, 9)
    .map(a => encodeURIComponent(a))
    .join('|');

  if (startFromStore) {
    const origin = encodeURIComponent(STORE_ADDRESS);
    return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&waypoints=${waypoints}&travelmode=driving`;
  }

  // Cihazın mevcut konumundan başlayıp sırayla duraklara ve son noktaya rota
  return `https://www.google.com/maps/dir/?api=1&destination=${destination}&waypoints=${waypoints}&travelmode=driving`;
};

/**
 * Adres veya şehir için Google Maps Embed URL'i üretir (iframe için).
 */
export const generateGoogleMapsEmbedUrl = (query = '') => {
  const cleanQuery = (query || STORE_ADDRESS).trim();
  return `https://maps.google.com/maps?q=${encodeURIComponent(cleanQuery)}&t=&z=11&ie=UTF8&iwloc=&output=embed`;
};

/**
 * Telefon numarasını temizler
 */
export const cleanPhoneNumber = (phone) => {
  if (!phone) return '';
  return String(phone).replace(/[^\d+]/g, '');
};

/**
 * Cihazın mobil veya tablet olup olmadığını tespit eder.
 * iPadOS Safari Mac olarak görünebilir ancak maxTouchPoints > 1'dir.
 */
export const isMobileOrTabletDevice = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent || '';
  const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  // iPadOS Safari tespiti (iPadOS 13+ masaüstü user-agent gönderir ancak dokunmatik ekranı vardır)
  const isIPad = /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;

  return isMobileUA || isIPad;
};

/**
 * WhatsApp mesaj linki oluşturur.
 * - Masaüstü / Bilgisayarda: Doğrudan WhatsApp Web'e gider (aradaki yönlendirme landing sayfasını atlar).
 * - Mobil / Tablette: Doğrudan WhatsApp uygulamasını açar.
 */
export const getWhatsAppDirectUrl = (phone, customerName = '', eventType = 'kesif', dateDisplay = '') => {
  if (!phone) return null;
  let clean = cleanPhoneNumber(phone);
  if (clean.startsWith('0')) {
    // Hollanda varsayılanı 06 -> 316
    clean = '31' + clean.substring(1);
  } else if (!clean.startsWith('+') && !clean.startsWith('31') && !clean.startsWith('90')) {
    clean = '31' + clean;
  }
  clean = clean.replace('+', '');

  const greeting = `Merhaba ${customerName}, Ebrar Gordijnen'den yazıyoruz.`;
  let msg = '';
  if (eventType === 'kesif') {
    msg = `${greeting} Keşif ve ölçüm randevunuz ile ilgili bilgi vermek istedik.${dateDisplay ? ` Planlanan Tarih: ${dateDisplay}.` : ''} Randevu saatini teyit edebilir misiniz?`;
  } else {
    msg = `${greeting} Perde siparişinizin teslimat ve montajı ile ilgili bilgi vermek istedik.${dateDisplay ? ` Planlanan Tarih: ${dateDisplay}.` : ''} Uygunluk durumunuzu teyit edebilir misiniz?`;
  }

  const encodedMsg = encodeURIComponent(msg);

  // Mobil veya Tablet cihaz kontrolü
  if (isMobileOrTabletDevice()) {
    return `https://api.whatsapp.com/send?phone=${clean}&text=${encodedMsg}`;
  }

  // Bilgisayarda doğrudan WhatsApp Web aç
  return `https://web.whatsapp.com/send?phone=${clean}&text=${encodedMsg}`;
};

/**
 * Tüm müşterileri analiz edip; Teslimat Hatırlatıcıları, Keşif Hatırlatıcıları
 * ve Bölgesel Fırsat Haritasını (Aynı gün keşif & montaj) hesaplar.
 */
export const calculateAllScheduleData = (customers = []) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const weekEnd = new Date(today);
  weekEnd.setDate(weekEnd.getDate() + 7);

  const twoWeeksEnd = new Date(today);
  twoWeeksEnd.setDate(twoWeeksEnd.getDate() + 14);

  // Kategorize edilecek yapılar
  const deliveryCategories = {
    overdue: [],
    today: [],
    tomorrow: [],
    thisWeek: [],
    upcoming: []
  };

  const inspectionCategories = {
    overdue: [],
    today: [],
    tomorrow: [],
    thisWeek: [],
    upcoming: []
  };

  const completedInspections = [];
  const allEvents = [];
  const regionalGroupsMap = new Map();

  // Yalnızca teslim edilmemiş (aktif) müşterileri değerlendir
  const activeCustomers = customers.filter(c => c && c.orderStatus !== 'delivered');

  activeCustomers.forEach(customer => {
    const loc = parseCustomerLocation(customer);
    const orderStatusLabel = ORDER_STATUS_LABELS[customer.orderStatus] || customer.orderStatus || 'Yeni Sipariş';

    // 1. TESLİMAT / MONTAJ İNCELEMESİ
    if (customer.deliveryDate) {
      const dDate = toNormalizedDate(customer.deliveryDate);
      if (dDate) {
        const daysLeft = Math.ceil((dDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        const timeStr = extractTimeString(customer.deliveryDate);

        let category = 'upcoming';
        let daysOverdue = 0;

        if (dDate < today) {
          category = 'overdue';
          daysOverdue = Math.abs(daysLeft);
        } else if (dDate.getTime() === today.getTime()) {
          category = 'today';
        } else if (dDate.getTime() === tomorrow.getTime()) {
          category = 'tomorrow';
        } else if (dDate < weekEnd) {
          category = 'thisWeek';
        } else if (dDate <= twoWeeksEnd) {
          category = 'upcoming';
        }

        const deliveryItem = {
          id: `montaj-${customer.id}`,
          customerId: customer.id,
          name: customer.name || 'İsimsiz Müşteri',
          phone: customer.phone1 || customer.phone2 || '',
          phone2: customer.phone2 || '',
          address: customer.address || '',
          city: loc.city,
          postalCode: loc.postalCode,
          regionKey: loc.regionKey,
          regionDisplay: loc.regionDisplay,
          rawDate: customer.deliveryDate,
          normalizedDate: dDate,
          timeStr,
          formattedDate: formatScheduleDateDisplay(customer.deliveryDate),
          daysLeft,
          daysOverdue,
          category,
          type: 'montaj',
          typeLabel: 'Teslimat & Montaj',
          typeIcon: 'fa-truck',
          orderStatus: customer.orderStatus,
          orderStatusLabel
        };

        if (deliveryCategories[category]) {
          deliveryCategories[category].push(deliveryItem);
        }
        allEvents.push(deliveryItem);

        // Bölge gruplamasına ekle
        if (!regionalGroupsMap.has(loc.regionKey)) {
          regionalGroupsMap.set(loc.regionKey, {
            regionKey: loc.regionKey,
            regionDisplay: loc.regionDisplay,
            city: loc.city,
            kesifList: [],
            montajList: []
          });
        }
        regionalGroupsMap.get(loc.regionKey).montajList.push(deliveryItem);
      }
    }

    // 2. KEŞİF / ÖLÇÜM İNCELEMESİ
    if (customer.inspectionDate) {
      const iDate = toNormalizedDate(customer.inspectionDate);
      if (iDate) {
        const isInspectionDone = !!(customer.inspectionCompleted || customer.isInspectionDone);
        const daysLeft = Math.ceil((iDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        const timeStr = extractTimeString(customer.inspectionDate);

        let category = 'upcoming';
        let daysOverdue = 0;

        if (iDate < today) {
          category = 'overdue';
          daysOverdue = Math.abs(daysLeft);
        } else if (iDate.getTime() === today.getTime()) {
          category = 'today';
        } else if (iDate.getTime() === tomorrow.getTime()) {
          category = 'tomorrow';
        } else if (iDate < weekEnd) {
          category = 'thisWeek';
        } else if (iDate <= twoWeeksEnd) {
          category = 'upcoming';
        }

        const inspectionItem = {
          id: `kesif-${customer.id}`,
          customerId: customer.id,
          name: customer.name || 'İsimsiz Müşteri',
          phone: customer.phone1 || customer.phone2 || '',
          phone2: customer.phone2 || '',
          address: customer.address || '',
          city: loc.city,
          postalCode: loc.postalCode,
          regionKey: loc.regionKey,
          regionDisplay: loc.regionDisplay,
          rawDate: customer.inspectionDate,
          normalizedDate: iDate,
          timeStr,
          formattedDate: formatScheduleDateDisplay(customer.inspectionDate),
          daysLeft,
          daysOverdue,
          category,
          type: 'kesif',
          typeLabel: 'Keşif & Ölçüm',
          typeIcon: 'fa-ruler-combined',
          orderStatus: customer.orderStatus,
          orderStatusLabel,
          isInspectionDone
        };

        if (isInspectionDone) {
          completedInspections.push(inspectionItem);
        } else {
          if (inspectionCategories[category]) {
            inspectionCategories[category].push(inspectionItem);
          }
          allEvents.push(inspectionItem);

          // Bölge gruplamasına ekle (sadece aktif olanlar)
          if (!regionalGroupsMap.has(loc.regionKey)) {
            regionalGroupsMap.set(loc.regionKey, {
              regionKey: loc.regionKey,
              regionDisplay: loc.regionDisplay,
              city: loc.city,
              kesifList: [],
              montajList: []
            });
          }
          regionalGroupsMap.get(loc.regionKey).kesifList.push(inspectionItem);
        }
      }
    }
  });

  // Sıralamalar
  const sortOverdue = (a, b) => b.daysOverdue - a.daysOverdue;
  const sortUpcoming = (a, b) => a.normalizedDate - b.normalizedDate;

  deliveryCategories.overdue.sort(sortOverdue);
  deliveryCategories.today.sort((a, b) => a.name.localeCompare(b.name));
  deliveryCategories.tomorrow.sort((a, b) => a.name.localeCompare(b.name));
  deliveryCategories.thisWeek.sort(sortUpcoming);
  deliveryCategories.upcoming.sort(sortUpcoming);

  inspectionCategories.overdue.sort(sortOverdue);
  inspectionCategories.today.sort((a, b) => a.name.localeCompare(b.name));
  inspectionCategories.tomorrow.sort((a, b) => a.name.localeCompare(b.name));
  inspectionCategories.thisWeek.sort(sortUpcoming);
  inspectionCategories.upcoming.sort(sortUpcoming);

  allEvents.sort((a, b) => {
    if (a.category === 'overdue' && b.category !== 'overdue') return -1;
    if (b.category === 'overdue' && a.category !== 'overdue') return 1;
    return a.normalizedDate - b.normalizedDate;
  });

  // Bölgesel Fırsat Analizi
  const regionalGroups = Array.from(regionalGroupsMap.values()).map(group => {
    // Liste içi sıralama
    group.kesifList.sort(sortUpcoming);
    group.montajList.sort(sortUpcoming);

    const totalCount = group.kesifList.length + group.montajList.length;
    // Hem keşif hem montaj varsa => AKILLI PLANLAMA FIRSATI!
    const hasOpportunity = group.kesifList.length > 0 && group.montajList.length > 0;

    // En erken tarih
    const allGroupDates = [
      ...group.kesifList.map(i => i.normalizedDate),
      ...group.montajList.map(m => m.normalizedDate)
    ];
    const earliestDate = allGroupDates.length > 0
      ? new Date(Math.min(...allGroupDates.map(d => d.getTime())))
      : null;

    // Tüm benzersiz adresler (Rota oluşturmak için)
    const addresses = Array.from(new Set([
      ...group.kesifList.map(k => k.address ? `${k.address}${k.city ? `, ${k.city}` : ''} Netherlands`.trim() : ''),
      ...group.montajList.map(m => m.address ? `${m.address}${m.city ? `, ${m.city}` : ''} Netherlands`.trim() : '')
    ])).filter(Boolean);

    return {
      ...group,
      totalCount,
      hasOpportunity,
      earliestDate,
      addresses,
      routeUrl: generateGoogleMapsUrl(addresses, { startFromStore: false }),
      storeRouteUrl: generateGoogleMapsUrl(addresses, { startFromStore: true })
    };
  });

  // Öncelik: 1) Fırsat olanlar (hem keşif hem montaj), 2) Toplam randevu sayısı çok olanlar, 3) En erken tarih
  regionalGroups.sort((a, b) => {
    if (a.hasOpportunity && !b.hasOpportunity) return -1;
    if (!a.hasOpportunity && b.hasOpportunity) return 1;
    if (b.totalCount !== a.totalCount) return b.totalCount - a.totalCount;
    if (a.earliestDate && b.earliestDate) return a.earliestDate - b.earliestDate;
    return a.regionDisplay.localeCompare(b.regionDisplay);
  });

  const totalMontajCount = Object.values(deliveryCategories).reduce((sum, arr) => sum + arr.length, 0);
  const totalKesifCount = Object.values(inspectionCategories).reduce((sum, arr) => sum + arr.length, 0);
  const opportunityCount = regionalGroups.filter(g => g.hasOpportunity).length;

  return {
    deliveryCategories,
    inspectionCategories,
    completedInspections,
    allEvents,
    regionalGroups,
    stats: {
      totalMontajCount,
      totalKesifCount,
      completedKesifCount: completedInspections.length,
      opportunityCount,
      overdueTotal: deliveryCategories.overdue.length + inspectionCategories.overdue.length,
      todayTotal: deliveryCategories.today.length + inspectionCategories.today.length,
      tomorrowTotal: deliveryCategories.tomorrow.length + inspectionCategories.tomorrow.length,
      thisWeekTotal: deliveryCategories.thisWeek.length + inspectionCategories.thisWeek.length
    }
  };
};
